import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import Spinner from "../../../components/Spinner";
import RightPanel from "../../../components/RightPanel";
import Table from "../../../components/Table";
import Select from "react-select";
import closeIcon from "../../../assets/img/icon_close.svg";
import excelIcon from "../../../assets/img/icon_excel.svg";
import { selectStyles, resetTime, resetTimeToEndOfDay } from "../../../util/common";
import { getAllLocationsAndRolesForCompanyHandler } from "../../../handlers/companiesHandlers/employeesHandlers/HandlerEmployeesDetails";
import * as StorageService from "../../../services/StorageServices";
import { LOCALSTORAGE_ITEMS, TYPES } from "../../../util/Constants";

export default function ServiceDocsManager({ companyId, registry, defaultDocType, onDocTypeNavigate }) {
  const navigate = useNavigate();

  const openedRightPanelStyleName = "open";
  const closedRightPanelStyleName = "closed";

  // -------- helpers ----------
  const firstRegistryOption = useMemo(() => {
    const first = Object.values(registry || {})[0];
    return first ? { label: first.label, value: first.value } : null;
  }, [registry]);

  const safeDefaultDocType = defaultDocType || firstRegistryOption;

  const [state, setState] = useState(() => ({
    loading: true,
    name: "",
    rows: [],
    allLocations: [],
    detailsId: null,
    rightPanelStyleName: closedRightPanelStyleName,
    panelSaved: false,
    selectedDocType: safeDefaultDocType,
    selectedLocation: { label: "Toate locațiile", value: "__ALL__" },
    isDeleteAlertOpen: false,
    selectedDeleteId: null,
    errorAlert: null,
    fromDate: resetTime(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
    toDate: resetTimeToEndOfDay(new Date()),
    currentPage: 0,
    totalPages: 1,
    totalElements: 0,
    reloadNonce: 0,
    sortDir: "DESC",
  }));
  const [exporting, setExporting] = useState(false);


  useEffect(() => {
    if (!companyId) return;

    const userType = StorageService.getItem(LOCALSTORAGE_ITEMS.TYPE);

    if (userType === TYPES.WORKER || userType === TYPES.MANAGER) {
      let locations = [];
      try {
        const raw = StorageService.getItem(LOCALSTORAGE_ITEMS.ACCOUNT_INFO);
        const employee = (raw ? JSON.parse(raw) : null)?.employee;

        if (userType === TYPES.MANAGER) {
          const merged = [
            ...(employee?.clockingLocations || []),
            ...(employee?.managedLocations || []),
          ];
          const byId = new Map(merged.map((loc) => [loc.id, loc]));
          locations = [...byId.values()];
        } else {
          locations = employee?.clockingLocations || [];
        }
      } catch (err) {
        console.error("ServiceDocsManager parse account_info", err);
      }
      setState((s) => ({ ...s, allLocations: locations }));
      return;
    }

    getAllLocationsAndRolesForCompanyHandler(companyId).then((resp) => {
      if (resp?.data?.error) return;
      setState((s) => ({ ...s, allLocations: resp.data.locations || [] }));
    });
  }, [companyId]);

  useEffect(() => {
    if (!safeDefaultDocType) return;

    setState((s) => {
      const cur = s.selectedDocType?.value;
      const next = safeDefaultDocType?.value;
      if (!next || cur === next) return s;

      return {
        ...s,
        selectedDocType: safeDefaultDocType,
        detailsId: "",
        rightPanelStyleName: closedRightPanelStyleName,
        isDeleteAlertOpen: false,
        selectedDeleteId: null,
        selectedLocation: { label: "Toate locațiile", value: "__ALL__" },
        currentPage: 0,
      };
    });

  }, [safeDefaultDocType?.value]);

  const active = useMemo(() => {
    const key = state.selectedDocType?.value;
    const fromSelected = key ? registry?.[key] : null;
    if (fromSelected) return fromSelected;

    const fallbackKey = safeDefaultDocType?.value;
    const fromDefault = fallbackKey ? registry?.[fallbackKey] : null;
    if (fromDefault) return fromDefault;

    return Object.values(registry || {})[0] || null;
  }, [registry, state.selectedDocType?.value, safeDefaultDocType?.value]);

  const effectiveDocType = useMemo(() => {
    if (state.selectedDocType?.value) return state.selectedDocType;
    if (safeDefaultDocType?.value) return safeDefaultDocType;
    return firstRegistryOption;
  }, [state.selectedDocType, safeDefaultDocType, firstRegistryOption]);

  const docTypeOptions = useMemo(
    () => Object.values(registry || {}).map((x) => ({ label: x.label, value: x.value })),
    [registry]
  );

  // -------- reload trigger (for save/delete) ----------
  const loadList = useCallback(() => {
    setState((s) => ({ ...s, reloadNonce: s.reloadNonce + 1, loading: true }));
  }, []);

  const handleServerSort = useCallback(() => {
    setState((s) => ({
      ...s,
      sortDir: s.sortDir === "DESC" ? "ASC" : "DESC",
      currentPage: 0,
      reloadNonce: s.reloadNonce + 1,
      loading: true,
    }));
  }, []);

  // -------- main fetch effect ----------
  useEffect(() => {
    if (!active) {
      setState((s) => ({ ...s, loading: false, rows: [] }));
      return;
    }

    setState((s) => ({ ...s, loading: true }));

    const createdFrom = state.fromDate.toISOString();
    const createdTo = state.toDate.toISOString();

    const locVal = state.selectedLocation?.value;
    const locationsIds = locVal && locVal !== "__ALL__" ? [locVal] : undefined;

    const sortField = active.sortField || "created";

    active
      .getAll({ createdFrom, createdTo, page: state.currentPage, sort: [`${sortField},${state.sortDir}`], locationsIds })
      .then((response) => {
        const data = response?.data || {};
        const pick =
          typeof active.pickListFromResponse === "function"
            ? active.pickListFromResponse
            : (d) => d?.items || d?.rows || d?.fisaServices || d?.bonuri || [];

        const raw = pick(data);
        const mapped = typeof active.mapList === "function" ? active.mapList(raw) : raw;

        const pageData = data?.page || {};
        const pageSize = pageData?.size ?? active.pageSize ?? mapped.length;
        const pageOffset = state.currentPage * pageSize;
        const finalMapped = mapped.map((row, idx) => ({ ...row, rowNo: pageOffset + idx + 1 }));

        setState((s) => ({
          ...s,
          loading: false,
          name: data?.name || "",
          rows: finalMapped,
          currentPage: pageData?.number ?? state.currentPage,
          totalPages: pageData?.totalPages ?? 1,
          totalElements: pageData?.totalElements ?? finalMapped.length,
        }));
      })
      .catch(() => setState((s) => ({ ...s, loading: false, rows: [] })));
  }, [active, state.currentPage, state.fromDate, state.toDate, state.reloadNonce, state.sortDir, state.selectedLocation?.value]);

  const openRightPanel = (id) => {
    setState((s) => ({
      ...s,
      detailsId: id ? String(id) : null,
      rightPanelStyleName: openedRightPanelStyleName,
      panelSaved: false,
    }));
  };

  // The panel header buttons close without arguments, so also reload when the
  // details component reported a save through onSaved.
  const closeRightPanel = (isSave) => {
    if (isSave || state.panelSaved) loadList();
    setState((s) => ({
      ...s,
      rightPanelStyleName: closedRightPanelStyleName,
      detailsId: null,
      panelSaved: false,
    }));
  };

  const openDeleteAlert = (id) => setState((s) => ({ ...s, isDeleteAlertOpen: true, selectedDeleteId: id }));

  const hideDeleteAlert = () => setState((s) => ({ ...s, isDeleteAlertOpen: false, selectedDeleteId: null }));

  const hideErrorAlert = () => setState((s) => ({ ...s, errorAlert: null }));

  const doDelete = async () => {
    if (!active) return;
    const id = state.selectedDeleteId;
    if (!id) return;

    try {
      const resp = await active.del(id);
      if (resp?.data?.error) {
        // Failures the doc type wants shown in a dialog carry an alertTitle.
        if (resp.data.alertTitle) {
          setState((s) => ({
            ...s,
            isDeleteAlertOpen: false,
            selectedDeleteId: null,
            errorAlert: { title: resp.data.alertTitle, text: resp.data.message },
          }));
          return;
        }

        alert(resp.data.message || "Ștergerea a eșuat.");
        hideDeleteAlert();
        return;
      }
      hideDeleteAlert();
      loadList();
    } catch (err) {
      console.error("doDelete", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Nu ai permisiunea necesară pentru a șterge acest document.";
      hideDeleteAlert();
      alert(msg);
    }
  };

  const exportToExcel = async () => {
    if (!active?.exportExcel) return;

    const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const fileName = `${active.exportFileName || "export"}-${toYmd(state.fromDate)}-${toYmd(state.toDate)}`;
    const locVal = state.selectedLocation?.value;

    setExporting(true);
    try {
      const resp = await active.exportExcel({
        createdFrom: state.fromDate.toISOString(),
        createdTo: state.toDate.toISOString(),
        sort: [`${active.sortField || "created"},${state.sortDir}`],
        locationsIds: locVal && locVal !== "__ALL__" ? [locVal] : undefined,
        fileName,
      });
      if (resp?.data?.error) {
        alert(resp.data.message || "Exportul a eșuat.");
        return;
      }
      const a = document.createElement("a");
      a.href = resp.data.file;
      a.download = fileName + ".xlsx";
      document.body.appendChild(a); // needed for firefox
      a.click();
      a.remove();
    } catch (err) {
      console.error("exportToExcel", err);
      alert("Exportul a eșuat.");
    } finally {
      setExporting(false);
    }
  };

  const getSelectedRow = () => state.rows.find((r) => r.id === state.selectedDeleteId);

  const getDeleteAlert = () => {
    if (!state.isDeleteAlertOpen || !active) return null;
    const row = getSelectedRow();

    const title = active.deleteAlertTitle || "Ștergere";
    const text = active.deleteAlertText || "Ești sigur?";
    const labelFn = active.getDeleteLabel || ((r) => r?.id);
    const extra = row ? ` (${labelFn(row)})` : "";

    return {
      title,
      contentRow1: `${text}${extra}`,
      deleteAction1: doDelete,
      deleteText1: "Șterge",
      cancelAction: hideDeleteAlert,
      cancelText: "Anulează",
      hideAlert: hideDeleteAlert,
    };
  };

  const getErrorAlert = () => {
    if (!state.errorAlert) return null;

    return {
      title: state.errorAlert.title,
      contentRow1: state.errorAlert.text,
      cancelAction: hideErrorAlert,
      cancelText: "Închide",
      hideAlert: hideErrorAlert,
    };
  };

  const onDocTypeChange = (opt) => {
    if (!opt?.value) return;

    const currentValue = state.selectedDocType?.value ?? safeDefaultDocType?.value ?? firstRegistryOption?.value;

    if (opt.value === currentValue) {
      setState((s) => ({
        ...s,
        rows: [],
        detailsId: "",
        rightPanelStyleName: closedRightPanelStyleName,
        isDeleteAlertOpen: false,
        selectedDeleteId: null,
        selectedLocation: { label: "Toate locațiile", value: "__ALL__" },
        currentPage: 0,
      }));

      loadList();
      return;
    }

    setState((s) => ({
      ...s,
      rows: [],
      selectedDocType: opt,
      detailsId: "",
      rightPanelStyleName: closedRightPanelStyleName,
      isDeleteAlertOpen: false,
      selectedDeleteId: null,
      selectedLocation: { label: "Toate locațiile", value: "__ALL__" },
      currentPage: 0,
    }));

    if (typeof onDocTypeNavigate === "function") {
      onDocTypeNavigate(opt);
      return;
    }

    const next = registry?.[opt.value];
    if (next?.path) navigate(next.path);
  };


  const locationOptions = useMemo(() => {
    const options = (state.allLocations || [])
      .map((loc) => ({ value: String(loc.id), label: loc.name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [{ label: "Toate locațiile", value: "__ALL__" }, ...options];
  }, [state.allLocations]);

  const onLocationChange = (opt) => {
    const next = opt?.value ? opt : { label: "Toate locațiile", value: "__ALL__" };
    if (next.value === state.selectedLocation?.value) return;
    setState((s) => ({
      ...s,
      selectedLocation: next,
      currentPage: 0,
      loading: true,
      detailsId: null,
      rightPanelStyleName: closedRightPanelStyleName,
      isDeleteAlertOpen: false,
      selectedDeleteId: null,
    }));
  };

  if (!active || !effectiveDocType) return <Spinner />;
  if (state.loading) return <Spinner />;

  const DetailsComponent = active.DetailsComponent;
  const userType = StorageService.getItem(LOCALSTORAGE_ITEMS.TYPE);
  const canExport =
    [TYPES.MANAGER, TYPES.ADMIN, TYPES.SUPER_ADMIN].includes(userType) && typeof active.exportExcel === "function";

  return (
    <div className="content">
      <Header alert={getDeleteAlert()} alert2={getErrorAlert()} companyId={companyId} hasProfileBtn={true} headerTitle={active.headerTitle} totalCount={state.totalElements} />

      <div className="inner-content inner-content-for-table">
        <div className="empty-div" style={{ height: 25 }} />

        <Table
          key={effectiveDocType?.value}
          isDocsTable
          headerActions={
            canExport && (
              <img
                src={excelIcon}
                alt="Export Excel"
                title="Export Excel"
                onClick={exporting ? undefined : exportToExcel}
                style={{ display: "block", width: 24, height: 24, cursor: exporting ? "default" : "pointer", opacity: exporting ? 0.5 : 1 }}
              />
            )
          }
          topActions={
            <section className="top-actions">
              {docTypeOptions.length > 1 && (
                <div className="form-group select-form-group">
                  <div className="row">
                    <label className="label">Tip document</label>
                  </div>
                  <div className="row">
                    <Select
                      className="select"
                      options={docTypeOptions}
                      onChange={onDocTypeChange}
                      placeholder={"Alege tipul"}
                      id="docType"
                      styles={selectStyles}
                      value={effectiveDocType}
                      components={{ IndicatorSeparator: () => null }}
                    />
                  </div>
                </div>
              )}

              <div className="form-group select-form-group">
                <div className="row">
                  <label className="label">Locație</label>
                </div>
                <div className="row">
                  <Select
                    className="select"
                    options={locationOptions}
                    onChange={onLocationChange}
                    placeholder={"Alege locația"}
                    id="locationFilter"
                    styles={selectStyles}
                    value={state.selectedLocation}
                    components={{ IndicatorSeparator: () => null }}
                    isDisabled={locationOptions.length <= 1}
                  />
                </div>
              </div>

              <div className="form-group select-form-group">
                <div className="row">
                  <label className="label">De la</label>
                </div>
                <div className="row">
                  <input
                    type="date"
                    className="input-date"
                    value={(() => { const d = state.fromDate; return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })()}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const d = resetTime(new Date(e.target.value + "T12:00:00"));
                      setState((s) => ({ ...s, fromDate: d, currentPage: 0, loading: true }));
                    }}
                  />
                </div>
              </div>

              <div className="form-group select-form-group" style={{ marginLeft: 12 }}>
                <div className="row">
                  <label className="label">Până la</label>
                </div>
                <div className="row">
                  <input
                    type="date"
                    className="input-date"
                    value={(() => { const d = state.toDate; return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })()}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const d = resetTimeToEndOfDay(new Date(e.target.value + "T12:00:00"));
                      setState((s) => ({ ...s, toDate: d, currentPage: 0, loading: true }));
                    }}
                  />
                </div>
              </div>

              {active.canAdd !== false && (
                <>
                  <button className="btn btn-success only-for-desktop" style={{ minWidth: 85 }} onClick={() => openRightPanel("")}>
                    {active.addBtnText}
                  </button>

                  <button className="btn btn-success only-for-small-screens" onClick={() => openRightPanel("")}>
                    <img src={closeIcon} alt={"close icon"} />
                  </button>
                </>
              )}
            </section>
          }
          columns={active.columns}
          data={state.rows}
          onSelectedRowId={(rowId) => openRightPanel(state.rows[rowId].id)}
          deleteAction={(rowId) => openDeleteAlert(state.rows[rowId].id)}
          selectedItemId={state.detailsId}
          searchBoxPlaceHolder={active.searchBoxPlaceHolder ?? "Chitanță, telefon"}
          searchBoxLabel="Căutare"
          filterAccessor={null}
          filterValue={null}
          filterFunction={(tv, v) => tv && tv.indexOf(v) >= 0}
          closeRightPanel={closeRightPanel}
          extraClassName={"service-docs-table"}
          multiFilter={true}
          onServerSort={handleServerSort}
          serverSortField={active.sortField || "created"}
          serverSortDir={state.sortDir}
          searchContainerStyle={{ width: 170 }}
        />

        {state.totalPages > 1 && (
          <div className="row" style={{ justifyContent: "center", alignItems: "center", padding: "15px 0", gap: "15px" }}>
            <button
              className={`btn btn-default no-min-width ${state.currentPage === 0 ? "btn-disabled-light" : ""}`}
              disabled={state.currentPage === 0}
              onClick={() => setState((s) => ({ ...s, currentPage: s.currentPage - 1, loading: true }))}
            >
              « Anterior
            </button>
            <span style={{ fontWeight: 600 }}>
              {state.currentPage + 1} / {state.totalPages}
            </span>
            <button
              className={`btn btn-default no-min-width ${state.currentPage >= state.totalPages - 1 ? "btn-disabled-light" : ""}`}
              disabled={state.currentPage >= state.totalPages - 1}
              onClick={() => setState((s) => ({ ...s, currentPage: s.currentPage + 1, loading: true }))}
            >
              Următor »
            </button>
          </div>
        )}


      </div>

      <RightPanel
        isOpened={state.rightPanelStyleName}
        closeRightPanel={closeRightPanel}
        fullScreen={true}
        title={null}
        hasBackBtn={true}
      >
        {state.rightPanelStyleName === openedRightPanelStyleName && DetailsComponent ? (
          <DetailsComponent
            key={state.detailsId ?? "new"}
            companyId={companyId}
            documentId={state.detailsId}
            closeRightPanel={closeRightPanel}
            onSaved={() => setState((s) => ({ ...s, panelSaved: true }))}
          />
        ) : null}
      </RightPanel>
    </div>
  );
}
